import { Injectable } from '@angular/core';
import {RequestService} from '../request/request.service';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {

  public static USERPROFILE_UPDATE_TOPIC = "userprofile.update";

  private userProfile: any = null;

  private pendingRequests = [];

  private pendingResponse = false;

  constructor(private requestService: RequestService) { }

  /**
   * Loads the user profile, allowing for caching and multiple concurrent requests.
   *
   * In the case of multiple concurrent requests, only a single HTTP request will be issued, and all of the clients
   * will have the value returned when the in-flight request is completed.
   *
   * @param forceReload
   */
  public getUserProfile(forceReload: boolean = false) {
    return new Promise<Map<string, any>>((resolve, reject) => {
      if (forceReload === false && this.userProfile != null) {
        resolve(this.userProfile);
      } else {
        this.pendingRequests.push({ resolve: resolve, reject: reject });
        this._getUserProfileCallback();
      }
    });
  }

  public async updateUserProfileSetting(key: string, value: any) {
    let result = "";

    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("updateUserProfileSetting")
        .data({key, value})
        .execute();

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception saving user dashboard id: ", e);
      result = "error";
    }

    return result;
  }

  public async getTenantFilteringOptions(searchText: string)  {
    let result = "";

    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("getTenancyFilterProfile")
        .data({ searchText: searchText })
        .execute();

      const responseData: any = response.data;

      return responseData;
    } catch (e) {
      console.error("Exception saving user dashboard id: ", e);
      result = "error";
    }

    return result;
  }

  public async getRecentImpersonations()  {
    let result = "";

    try {
      const response: any = await this.requestService.createRequest()
          .controller("servicely.core.User")
          .method("getRecentImpersonations")
          .data({})
          .execute();

      const responseData: any = response.data;
      return responseData;
    } catch (e) {
      console.error("Exception in getRecentImpersonations: ", e);
      result = "error";
    }

    return result;
  }

  public async setTenantFilteringOptions(option)  {
    let result = "";

    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("setTenancyFilterProfile")
        .data(option)
        .async(true)
        .execute();

      const responseData: any = response.data;

      return responseData;
    } catch (e) {
      console.error("Exception saving user dashboard id: ", e);
      result = "error";
    }

    return result;
  }

  private _getUserProfileCallback() {
    // Only perform one request at a time
    if (this.pendingResponse === false) {
      this.pendingResponse = true;

      this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("getUserProfile")
        .data({})
        .execute()
        .then(result => {
          const responseData = result["data"];

          this.userProfile = responseData;

          // Clone the pending request queue so we can clear it out before responding to the promises
          const allRequests = this.pendingRequests.map(it => it);
          this.pendingRequests = [];

          // Set the response pending flag back to false so new requests will be actioned.
          this.pendingResponse = false;

          // Resolve the promises
          allRequests.forEach(it => it.resolve(responseData));

          // Allow listeners to respond to an update from another source
          this.sendUserProfileUpdateMessage(responseData);
        })
        .catch(err => {
          const allRequests = this.pendingRequests.map(it => it);
          this.pendingRequests = [];
          this.pendingResponse = false;
          allRequests.forEach(it => it.reject(err));
        });
    }
  }

  private sendUserProfileUpdateMessage(data: any) {
    const MessageBus = (window as any).MessageBus;
    MessageBus.publish(MessageBus.CHANNELS.MESSAGE_BUS_BRIDGE_API, UserProfileService.USERPROFILE_UPDATE_TOPIC, data);
  }
}
